import React, { PureComponent, RefObject, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import mapboxgl, { GeoJSONSource, LngLat } from 'mapbox-gl';
import { withRouter, RouteComponentProps } from 'react-router';
import useMediaQuery from '@material-ui/core/useMediaQuery'
import 'mapbox-gl/dist/mapbox-gl.css'

import './map.css'
import { IToursMapItem } from '../../store';
import { Feature, Point } from 'geojson';
import { PitchToggle, StreetSatelliteToggle } from './controls';
import Preview from './preview'

mapboxgl.accessToken = 'pk.eyJ1Ijoid293emFhYS1pbyIsImEiOiJjanRlbHF5amkwMGlyNDRwM2dxdWx3cXl5In0.NMe_64dWjrwviwDz4-8inw';
const TOURS_SOURCE_NAME = "tours"

interface IMapViewProps {
    data: IToursMapItem[]
}

export const MapView = withRouter((props: RouteComponentProps & IMapViewProps) => {
    const { match, history, data } = props
    const mapContainer = useRef<HTMLDivElement>(null)
    const [previewTour, setPreviewTour] = useState<IToursMapItem>()
    const [mapInitialized, setMapInitialized] = useState(false)
    const isMobileDevice = useMediaQuery('(max-width:480px)');

    const map = useMemo(() => {
        if (mapContainer.current) {
            const map = new mapboxgl.Map({
                container: mapContainer.current as HTMLDivElement,
                style: 'mapbox://styles/wowzaaa-io/cjxeiow9908h51clf8qou5nmr',
                // trackResize: true,
                // interactive: false
            })

            return map
        }
        return null
    }, [mapContainer.current]);

    const addLayers = useCallback(() => {
        if (!map || map.getLayer("clusters")) return

        map.addLayer({
            id: "clusters",
            type: "circle",
            source: TOURS_SOURCE_NAME,
            filter: ["has", "point_count"],
            paint: {
                // Use step expressions (https://www.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                // with three steps to implement three types of circles:
                //   * Blue, 20px circles when point count is less than 100
                //   * Yellow, 30px circles when point count is between 100 and 750
                //   * Pink, 40px circles when point count is greater than or equal to 750
                "circle-color": [
                    "step",
                    ["get", "point_count"],
                    "#51bbd6",
                    100,
                    "#f1f075",
                    750,
                    "#f28cb1"
                ],
                "circle-radius": [
                    "step",
                    ["get", "point_count"],
                    20,
                    100,
                    30,
                    750,
                    40
                ]
            }
        })
        map.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: TOURS_SOURCE_NAME,
            filter: ["has", "point_count"],
            layout: {
                "text-field": "{point_count_abbreviated}",
                "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                "text-size": 12
            }
        })
        map.addLayer({
            id: "unclustered-point",
            type: "symbol",
            source: TOURS_SOURCE_NAME,
            filter: ["!", ["has", "point_count"]],
            layout: {
                "icon-image": "{icon}",
                "icon-size": 1,
                "icon-anchor": 'bottom',
                "icon-ignore-placement": true,
                "icon-allow-overlap": true
            }
        })
    }, [map])

    const fitBounds = useCallback((data: IToursMapItem[]) => {
        if (map && data.length) {
            const bounds = new mapboxgl.LngLatBounds()
            data.forEach(({ lat, lng}) => bounds.extend(new LngLat(lng, lat)))
            map.fitBounds(bounds, { padding: 25 });
        }
    }, [map])

    const drawMap = useCallback(() => {
        if (map && data) {
            addData(data)
            addLayers()
            map.once('idle', () => {
                fitBounds(data);
            })
        }
    }, [map, data])

    const toFeatures = useCallback((data: IToursMapItem[]) => {
        return data.map(({id, lat, lng, categoryId}) => {
            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lng, lat]
                },
                properties: {
                    id: id,
                    icon: `c${categoryId}`
                }
            } as Feature<Point>
        })
    }, [])

    const addData = useCallback((data: IToursMapItem[]) => {
        if (!map) return
        let source = map.getSource('tours') as GeoJSONSource;
        if (source) {
            source.setData({
                type: "FeatureCollection",
                features: toFeatures(data)
            });
        } else {
            map.addSource(TOURS_SOURCE_NAME, {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: toFeatures(data)
                },
                cluster: true,
                clusterRadius: 50
            });
        }
    }, [map])

    const loadImages = useCallback(async () => {
        if (!map || !data) return
        const categories = [...new Set(data.map(item => item.categoryId))]
        const currentImages = map.listImages()
        const toLoad = categories.filter(item => currentImages.indexOf(`c${item}`) === -1)
        if (toLoad.length) {
            await Promise.all(
                toLoad.map(item => new Promise<void>((resolve, reject) => {
                    map.loadImage(`https://assets.360tourist.com/images/pins/categories/${item}.png`, (error: any, image: any ) => {
                        map.addImage(`c${item}`, image)
                        resolve()
                    })
                })) 
            )
        }
    }, [map, data])

    const attachMapEvents = useCallback(() => {
        if (!map) return
        // Clusters events
        map.on('click', 'clusters', (e) => {
            //if (window.event && window.event.stopPropagation) window.event.stopPropagation()
            //else if (window.Event && window.Event.stopPropagation) window.Event.stopPropagation()
            var features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
            if (features[0].properties) {
                (map.getSource(TOURS_SOURCE_NAME) as GeoJSONSource).getClusterExpansionZoom(features[0].properties.cluster_id, (err, zoom) => {
                    if (err) return
    
                    const [lat, lng] = (features[0].geometry as Point).coordinates
                    map.easeTo({
                        duration: 1000,
                        center: new LngLat(lat, lng),
                        zoom: zoom
                    })
                })
            }
        })

        map.on('mouseenter', 'clusters', () => {
            map.getCanvas().style.cursor = 'pointer'
        })
        map.on('mouseleave', 'clusters', () => {
            map.getCanvas().style.cursor = ''
        })

        // Unclustered events

        map.on('mouseenter', 'unclustered-point', () => {
            map.getCanvas().style.cursor = 'pointer'
        })
        map.on('mouseleave', 'unclustered-point', () => {
            map.getCanvas().style.cursor = ''
        })

        map.on('style.load', e => {
            loadImages().then(drawMap)
        })
    }, [map])

    useEffect(() => {
        if (map) {
            map.once('load', () => {
                map.addControl(new mapboxgl.NavigationControl(), 'bottom-left');
                map.addControl(new PitchToggle({ minpitchzoom: 11 }), 'bottom-left')
                map.addControl(new StreetSatelliteToggle(), 'bottom-left');
                
                attachMapEvents();
                setMapInitialized(true)
            })

            return () => {
                map.remove()
                setMapInitialized(false)
            }
        }
    }, [map])

    useEffect(() => {
        if (map && mapInitialized && data) {
            loadImages().then(drawMap)
        }
        
    }, [map, mapInitialized, data])

    useEffect(() => {
        if (!map || !data) return
        const handleClickUnclusteredPoint = (e: any) => {
            if (e.features) {
                const { id } = e.features[0].properties as { id: number }
                const tourData = data.find(item => item.id === id)
                if (tourData) {
                    if (isMobileDevice) {
                        setPreviewTour(tourData)
                    } else {
                        const { language = 'en' } = match.params as { language: string}
                        history.push(`/${language}/${tourData.countrySlug}/${tourData.regionSlug}/${tourData.citySlug}/${tourData.slug}`, { ...props.location.state, tourId: id})
                    }
                }
            }
        }

        // Unclustered events
        map.on('click', 'unclustered-point', handleClickUnclusteredPoint)

        return () => {
            console.log("Cleanup click unclustered")
            map.off('click', 'unclustered-point', handleClickUnclusteredPoint)
        }
    }, [map, data, isMobileDevice])

    useEffect(() => {
        setPreviewTour(undefined)
    }, [data])

    return (
        <>
            <div ref={mapContainer} className="map-container"/>
            {previewTour && <Preview tour={previewTour} onClose={() => setPreviewTour(undefined)} />}
        </>
    )
})
